import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Stack, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useDirectory } from "@/lib/directory";

const remaining = (r) =>
  Math.round(((r.usable_sqft || 0) - (r.allocated_sqft || 0)) * 10) / 10;

export const RemnantAllocation = ({ jobId }) => {
  const { nameOf } = useDirectory();
  const [allocations, setAllocations] = useState([]);
  const [options, setOptions] = useState([]);
  const [pick, setPick] = useState("");
  const [sqft, setSqft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [allocs, remnants] = await Promise.all([
      api.get(`/reservations/${jobId}/allocations`),
      api.get("/remnants"),
    ]);
    setAllocations(allocs.data.allocations);
    setOptions(remnants.data.filter((r) => remaining(r) > 0));
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedRemnant = options.find((o) => o.id === pick);

  const allocate = async () => {
    setBusy(true);
    try {
      await api.post(`/remnants/${pick}/allocate`, {
        reservation_id: jobId,
        sqft: Number(sqft),
      });
      toast.success(`${sqft} ft² pulled from ${selectedRemnant?.code}`);
      setPick("");
      setSqft("");
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Allocation failed",
      );
    }
    setBusy(false);
  };

  const release = async (a) => {
    await api.delete(`/remnants/${a.remnant_id}/allocate/${a.id}`);
    toast.success("Allocation released back to stock");
    load();
  };

  const totalPulled =
    Math.round(allocations.reduce((sum, a) => sum + (a.sqft || 0), 0) * 10) /
    10;

  return (
    <div
      className="no-print rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid="remnant-allocation"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs uppercase tracking-widest text-zinc-500">
          Remnant Material
        </div>
        <span
          className="font-mono-data text-sm font-bold text-yellow-400"
          data-testid="allocated-total"
        >
          {totalPulled} ft² pulled
        </span>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-[2fr_1fr_auto]">
        <Select value={pick} onValueChange={setPick}>
          <SelectTrigger
            data-testid="allocation-remnant-select"
            className="h-12 border-zinc-800 bg-zinc-950"
          >
            <SelectValue placeholder="Choose a remnant" />
          </SelectTrigger>
          <SelectContent className="border-zinc-800 bg-zinc-900">
            {options.length === 0 && (
              <div className="px-3 py-2 text-sm text-zinc-500">
                No remnants with usable area
              </div>
            )}
            {options.map((r) => (
              <SelectItem
                key={r.id}
                value={r.id}
                data-testid={`allocation-option-${r.code}`}
              >
                {r.code} — {r.slab_color} ({remaining(r)} ft² left)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div>
          <Label className="sr-only">Square feet</Label>
          <Input
            data-testid="allocation-sqft-input"
            type="number"
            step="0.1"
            min="0.1"
            max={selectedRemnant ? remaining(selectedRemnant) : undefined}
            value={sqft}
            onChange={(e) => setSqft(e.target.value)}
            placeholder={
              selectedRemnant ? `max ${remaining(selectedRemnant)}` : "sq ft"
            }
            className="h-12 border-zinc-800 bg-zinc-950"
          />
        </div>
        <Button
          data-testid="allocate-button"
          onClick={allocate}
          disabled={busy || !pick || !sqft}
          className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
        >
          <Stack size={18} weight="bold" className="mr-2" /> Pull
        </Button>
      </div>

      <div className="mt-4 space-y-2" data-testid="allocation-list">
        {allocations.length === 0 && (
          <p className="text-sm text-zinc-500">
            No remnant material pulled for this job yet.
          </p>
        )}
        {allocations.map((a) => (
          <div
            key={a.id}
            data-testid={`allocation-row-${a.id}`}
            className="flex items-center gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3"
          >
            <div className="min-w-0 flex-1">
              <Link
                to={`/inventory/remnants/${a.remnant_id}`}
                className="font-mono-data text-xs text-yellow-500 hover:underline"
              >
                {a.code}
              </Link>
              <div className="truncate text-sm">{a.slab_color}</div>
              <div className="text-xs text-zinc-500">
                Rack {a.location || "—"} · {nameOf(a.created_by)}
              </div>
            </div>
            <span className="font-mono-data text-sm font-bold">
              {a.sqft} ft²
            </span>
            <button
              type="button"
              data-testid={`release-allocation-${a.id}`}
              onClick={() => release(a)}
              className="rounded-md p-2 text-zinc-500 transition-colors duration-150 hover:bg-red-950/40 hover:text-red-400"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
